import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

type Props = {
  open: boolean;
  task_id: number;
  title: string;
  fcInclusionStatus: any;
  setFcInclusionStatus: any;
  onCancel: () => void;
  onClose: () => void;
};

const defaultMessage = 'Provide a new needs by date and/or days supply for this therapy';

export default function TherapyOverrideModal(props: Props) {
  const { open, task_id, title, fcInclusionStatus, setFcInclusionStatus, onCancel, onClose } =
    props;

  const [needsByDate, setNeedsByDate] = React.useState(fcInclusionStatus?.[task_id]?.new_nbd || '');
  const [daysSupply, setDaysSupply] = React.useState<number | null>(
    fcInclusionStatus?.[task_id]?.ds ?? null,
  );

  const isDaysSupplyValid =
    typeof daysSupply === 'number' && Number.isFinite(daysSupply) && daysSupply >= 0;

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setNeedsByDate(fcInclusionStatus?.[task_id]?.new_nbd || '');
    setDaysSupply(fcInclusionStatus?.[task_id]?.ds ?? null);
  }, [open, task_id, fcInclusionStatus]);

  const handleClose = (event: React.SyntheticEvent, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleRemove = () => {
    if (!isDaysSupplyValid) {
      return;
    }

    setFcInclusionStatus({
      ...fcInclusionStatus,
      [task_id]: {
        ...fcInclusionStatus?.[task_id],
        new_nbd: needsByDate === '' ? null : needsByDate,
        ds: daysSupply,
      },
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      aria-labelledby="therapy-override-title"
      PaperProps={{
        sx: {
          maxWidth: 'none',
          width: 'max-content',
        },
      }}
    >
      <DialogTitle id="therapy-override-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 0 }}>
        <Box
          sx={{
            border: '1px solid #D2D2D2',
            borderRadius: '4px',
            background: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              background: '#F2F2F2',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <Typography>{defaultMessage}</Typography>
          </Box>
          <Box sx={{ padding: '16px 20px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography>Needs By Date</Typography>
                <TextField
                  type="date"
                  variant="standard"
                  fullWidth
                  value={needsByDate}
                  onChange={event => setNeedsByDate(event.target.value)}
                  sx={{ marginTop: '4px' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>Days Supply</Typography>
                <TextField
                  variant="standard"
                  fullWidth
                  type="number"
                  value={daysSupply ?? ''}
                  error={!isDaysSupplyValid}
                  helperText={!isDaysSupplyValid ? 'Enter 0 or greater.' : ' '}
                  onChange={event =>
                    setDaysSupply(event.target.value === '' ? null : Number(event.target.value))
                  }
                  inputProps={{ min: 0 }}
                  sx={{ marginTop: '4px' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '8px 24px 16px' }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRemove}
          color="primary"
          disabled={!isDaysSupplyValid}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
